import * as React from 'react'
import { useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import {
  getRememberedScrollPosition,
  rememberScrollPosition,
} from '@/utils/scroll-position-memory'

const LayoutContext = React.createContext<{
  offset: number
  fixed: boolean
} | null>(null)

interface LayoutProps extends React.HTMLAttributes<HTMLDivElement> {
  fixed?: boolean
  /**
   * Remembers this page's scroll position (keyed by pathname) and restores it
   * when the page remounts, e.g. after navigating Back from a detail page.
   */
  restoreScroll?: boolean
}

const Layout = ({
  className,
  fixed = false,
  restoreScroll = false,
  ...props
}: LayoutProps) => {
  const divRef = React.useRef<HTMLDivElement>(null)
  const [offset, setOffset] = React.useState(0)
  const { pathname } = useLocation()

  React.useEffect(() => {
    const div = divRef.current

    if (!div) return
    const onScroll = () => setOffset(div.scrollTop)

    // clean up code
    div.removeEventListener('scroll', onScroll)
    div.addEventListener('scroll', onScroll, { passive: true })
    return () => div.removeEventListener('scroll', onScroll)
  }, [])

  React.useEffect(() => {
    if (!restoreScroll) return
    const div = divRef.current
    if (!div) return

    const target = getRememberedScrollPosition(pathname)
    let attempts = 0
    const applyScroll = () => {
      if (!div || div.scrollTop >= target) return
      div.scrollTop = target
      attempts += 1
      if (attempts < 20) requestAnimationFrame(applyScroll)
    }
    if (target > 0) requestAnimationFrame(applyScroll)

    // Save on every scroll frame (throttled via rAF) instead of on unmount:
    // by the time an unmount cleanup runs, the element is already detached
    // and its scrollTop/scrollHeight read back as 0, which would overwrite
    // the real position with 0.
    let ticking = false
    const onScroll = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        rememberScrollPosition(pathname, div.scrollTop)
        ticking = false
      })
    }
    div.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      div.removeEventListener('scroll', onScroll)
    }
  }, [pathname, restoreScroll])

  return (
    <LayoutContext.Provider value={{ offset, fixed }}>
      <div
        ref={divRef}
        data-layout='layout'
        className={cn(
          'h-full overflow-auto',
          fixed && 'flex flex-col',
          className
        )}
        {...props}
      />
    </LayoutContext.Provider>
  )
}
Layout.displayName = 'Layout'

interface HeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  sticky?: boolean
}

const Header = React.forwardRef<HTMLDivElement, HeaderProps>(
  ({ className, sticky, ...props }, ref) => {
    // Check if Layout.Header is used within Layout
    const contextVal = React.useContext(LayoutContext)
    if (contextVal === null) {
      throw new Error(
        `Layout.Header must be used within ${Layout.displayName}.`
      )
    }

    return (
      <div
        ref={ref}
        data-layout='header'
        className={cn(
          `z-10 flex flex-wrap items-center gap-4 bg-background`,
          contextVal.offset > 10 && sticky ? 'pb-2.5 shadow' : 'shadow-none',
          contextVal.fixed && 'flex-none',
          sticky && 'sticky top-0',
          className
        )}
        {...props}
      />
    )
  }
)
Header.displayName = 'Header'

const Body = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  // Check if Layout.Body is used within Layout
  const contextVal = React.useContext(LayoutContext)
  if (contextVal === null) {
    throw new Error(`Layout.Body must be used within ${Layout.displayName}.`)
  }

  return (
    <div
      ref={ref}
      data-layout='body'
      className={cn(
        // AROS-2383: was `md:pb-24` (96px) — on short lists that generous a
        // reserve alone was enough to push total content past the viewport
        // by a few px, forcing an otherwise-pointless page scrollbar. Needs
        // real-browser confirmation across a few pages (short + long lists)
        // that this still leaves enough breathing room below pagination.
        'px-1 py-4 md:overflow-hidden md:pb-10',
        // 'px-1 py-4 md:overflow-hidden md:px-4',
        contextVal && contextVal.fixed && 'flex-1',
        className
      )}
      {...props}
    />
  )
})
Body.displayName = 'Body'

Layout.Header = Header
Layout.Body = Body

export { Layout }
